import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import SystemSettings from "@/pages/admin/SystemSettings";
import BillingUpload from "@/pages/BillingUpload";
import SupportInbox from "@/pages/SupportInbox";
import LinkRequests from "@/pages/LinkRequests";
import Advisories from "@/pages/ops/Advisories";
import Reports from "@/pages/ops/Reports";
import Tickets from "@/pages/ops/Tickets";
import Announcements from "@/pages/ops/Announcements";
import DeletionRequests from "@/pages/ops/DeletionRequests";
import AdminSettings from "@/pages/admin/AdminSettings";
import { AccountingDashboard, TreasuryDashboard } from "@/pages/Dashboards";
import AppShell, { RequirePerm } from "@/components/AppShell";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Recover from "@/pages/Recover";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import { MyApplications, MyAccounts } from "@/pages/Consumer";
import ApplicationWizard from "@/pages/ApplicationWizard";
import { ApplicationsList, ApplicationReview } from "@/pages/Applications";
import { AccountsList, AccountDetail } from "@/pages/Accounts";
import { TwaDashboard, PaymentsPage, TaxRules } from "@/pages/Accounting";
import SecurityPolicy from "@/pages/admin/SecurityPolicy";
import { UsersPage, RecoveryRequests, AuditLog } from "@/pages/admin/Admin";
import RegistrationConfig from "@/pages/admin/RegistrationConfig";

const P = ({ perm, role, children }) => <RequirePerm perm={perm} role={role}>{children}</RequirePerm>;

function App() {
  return (
    <BrowserRouter>
      <BrandingProvider>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/recover" element={<Recover />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/app" element={<AppShell />}>
            <Route index element={<Dashboard />} />
            <Route path="profile" element={<Profile />} />
            <Route path="my-applications" element={<P role="consumer"><MyApplications /></P>} />
            <Route path="my-applications/:id" element={<P role="consumer"><ApplicationWizard /></P>} />
            <Route path="my-accounts" element={<P role="consumer"><MyAccounts /></P>} />
            <Route path="applications" element={<P perm="registration.review"><ApplicationsList /></P>} />
            <Route path="applications/:id" element={<P perm="registration.review"><ApplicationReview /></P>} />
            <Route path="accounts" element={<P perm={["accounts.view", "twa.view", "accounts.manage"]}><AccountsList /></P>} />
            <Route path="accounts/:id" element={<P perm={["accounts.view", "twa.view"]}><AccountDetail /></P>} />
            <Route path="twa" element={<P perm="twa.view"><TwaDashboard /></P>} />
            <Route path="payments" element={<P perm={["payments.record", "payments.wht_review"]}><PaymentsPage /></P>} />
            <Route path="tax-rules" element={<P perm="twa.view"><TaxRules /></P>} />
            <Route path="admin/users" element={<P perm="users.view"><UsersPage /></P>} />
            <Route path="admin/security-policy" element={<P perm="policy.manage"><SecurityPolicy /></P>} />
            <Route path="admin/recovery" element={<P perm="recovery.manage"><RecoveryRequests /></P>} />
            <Route path="admin/registration-config" element={<P perm="registration.configure"><RegistrationConfig /></P>} />
            <Route path="admin/audit" element={<P perm="audit.view"><AuditLog /></P>} />
            <Route path="admin/admin-settings" element={<P perm="admin_settings.manage"><AdminSettings /></P>} />
            <Route path="admin/system-settings" element={<P perm="settings.manage"><SystemSettings /></P>} />
            <Route path="billing-upload" element={<P perm="billing.upload"><BillingUpload /></P>} />
            <Route path="support" element={<P perm={["support.inbox", "support.team"]}><SupportInbox /></P>} />
            <Route path="support/:id" element={<P perm={["support.inbox", "support.team"]}><SupportInbox /></P>} />
            <Route path="link-requests" element={<P perm="links.review"><LinkRequests /></P>} />
            <Route path="advisories" element={<P perm="outages.manage"><Advisories /></P>} />
            <Route path="reports" element={<P perm="reports.manage"><Reports /></P>} />
            <Route path="tickets" element={<P perm="tickets.manage"><Tickets /></P>} />
            <Route path="announcements" element={<P perm="announcements.manage"><Announcements /></P>} />
            <Route path="deletion-requests" element={<P perm="deletion.review"><DeletionRequests /></P>} />
            <Route path="dashboards/accounting" element={<P perm="accounting.dashboard"><AccountingDashboard /></P>} />
            <Route path="dashboards/treasury" element={<P perm="treasury.dashboard"><TreasuryDashboard /></P>} />
          </Route>
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
        <Toaster richColors position="top-right" />
      </AuthProvider>
      </BrandingProvider>
    </BrowserRouter>
  );
}

export default App;
